/**
 * Tiny "Get directions" mini-CTA that deep-links to Google Maps directions.
 *
 * Why this exists: renters arriving at unfamiliar pickup venues (airport
 * parking lots, vendor garages, hotel valet bays) routinely message hosts
 * asking "where exactly do I go?". Surfacing a one-tap directions link on
 * the confirmation/agreement screens cuts that support load and gives the
 * renter a reassuring next step right when they need it.
 *
 * Uses Google Maps' platform-agnostic search URL — opens the native maps
 * app on iOS/Android and google.com/maps on desktop. We pass `dir_action=navigate`
 * so mobile users land directly in the turn-by-turn navigation view.
 */
import { Navigation } from "lucide-react";

export default function DirectionsLink({
  location,
  className = "",
  size = "sm",
  testId = "directions-link",
}) {
  if (!location) return null;
  const href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}&dir_action=navigate`;
  const sizing =
    size === "xs"
      ? "px-1.5 py-0.5 text-[10px] gap-1"
      : "px-2 py-0.5 text-[11px] gap-1";
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 font-semibold transition ${sizing} ${className}`}
      title={`Get directions to ${location}`}
      data-testid={testId}
    >
      <Navigation className={size === "xs" ? "h-2.5 w-2.5" : "h-3 w-3"} />
      Directions
    </a>
  );
}
